import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
    Box,
    Button,
    TextField,
    Typography,
    CircularProgress,
    Radio,
    RadioGroup,
    FormControlLabel,
} from '@mui/material';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { getSalesReport } from '../bookingConfirmation/thunks/getSalesReports';
import moment from 'moment';
import swal from 'sweetalert';
import { getRefundReport } from '../bookingConfirmation/thunks/getRefundReport';



type FormValues = {
    pcc: string;
    date: string;
};

const RQBReportGenerator: React.FC = () => {
    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm<FormValues>();

    const [showLoader, setShowLoader] = useState(false);

    const [snackbar, setSnackbar] = React.useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error';
    }>({ open: false, message: '', severity: 'success' });
    const [selected, setSelected] = useState("dqb");

    const onSubmit = async (data: FormValues) => {
        if (showLoader) return; // Prevent multiple submissions
        let formatedDate = moment(data.date, 'DDMMMYYYY').format('YYYY-MM-DD');
        try {
            setShowLoader(true);
            let payload = {
                date: formatedDate,
                pcc: data.pcc,
            };
            console.log('Payload:', payload);
            let response;
            if (selected === "refund") {
                response = await getRefundReport(payload);
            }else{
                response = await getSalesReport(payload);
            }
            console.log('Response:', response);

            if (response && response.length > 0) {
                handleDownload(response, data.pcc, formatedDate);
            } else {
                swal({
                    title: "No Data Found",
                    text: response?.error?.message || "No data found for the given date and PCC.",
                    icon: "error",
                })
            }
            reset();
            setShowLoader(false);
        } catch (error) {
            setSnackbar({
                open: true,
                message: 'Failed to generate report.',
                severity: 'error',
            });
        }
    };

    const handleDownload = async (data: any, pcc: string, date: string) => {
        if (data && data.length !== 0) {
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Report');
            // Dynamically get column headers from the first object keys  
            const headers = Object.keys(data[0]);

            // Set columns dynamically
            worksheet.columns = headers.map((key) => ({
                header: key.toUpperCase(),
                key: key,
                width: 20,
            }));

            // Add rows
            data && Array.isArray(data) && data.forEach((row) => {
                worksheet.addRow(row);
            });
            // Create buffer and trigger download
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            saveAs(blob, `${pcc}-${date}.xlsx`);
        }
    }


    return (
        <Box maxWidth={400} mx="auto" mt={5}>
            
            <RadioGroup
                row
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
            >
                <FormControlLabel
                    value="dqb"
                    control={<Radio />}
                    label={
                        <Typography variant="subtitle1" sx={{ mb: 0 }}>
                            DQB Report Generator
                        </Typography>
                    }
                />
                <FormControlLabel
                    value="refund"
                    control={<Radio />}
                    label={
                        <Typography variant="subtitle1" sx={{ mb: 0 }}>
                            Refund Report
                        </Typography>
                    }
                />
            </RadioGroup>
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <TextField
                    label="PCC"
                    fullWidth
                    margin="normal"
                    {...register('pcc', {
                        required: 'PCC is required',
                        pattern: {
                            value: /^[A-Z0-9]{1,4}$/, // Alphanumeric, max 4 characters
                            message: 'PCC must be alphanumeric and up to 4 characters',
                        },
                        onChange: (e) => {
                            e.target.value = e.target.value.toUpperCase();
                        },
                    })}
                    error={!!errors.pcc}
                    helperText={errors.pcc?.message}
                    inputProps={{ maxLength: 4 }}
                />

                <TextField
                    label="Date (e.g., 05AUG2025)"
                    fullWidth
                    margin="normal"
                    {...register('date', {
                        required: 'Date is required',
                        pattern: {
                            value: /^(0[1-9]|[12][0-9]|3[01])[A-Z]{3}\d{4}$/,
                            message: 'Invalid date format. Use DDMMMYYYY (e.g., 05AUG2025)',
                        },
                        onChange: (e) => {
                            e.target.value = e.target.value.toUpperCase();
                        },
                    })}
                    error={!!errors.date}
                    helperText={errors.date?.message}
                    inputProps={{ maxLength: 9 }}
                />

                <Button 
                    variant="contained" 
                    color="primary" 
                    fullWidth 
                    type="submit"
                    disabled={showLoader}
                    startIcon={showLoader && <CircularProgress size={20} color="inherit" />}
                >
                    Generate Report
                </Button>
            </form>
        </Box>
    );
};

export default RQBReportGenerator;
